import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account, Notification, ScheduledAlert } from "model";

/**
 * @class NotificationDetail
 * @classdesc Class definition for NotificationDetail model class 
 * @exported true
 * @extends BCMSRecord 
 * @example const value = new NotificationDetail(record) 
 *
 * @param {OBJECT} record - record parameter
 *
 * @property {INTEGER} _id - The id property of a NotificationDetail 
 * @property {notification} _notification - The notification property of a NotificationDetail 
 * @property {account} _account - The account property of a NotificationDetail 
 * @property {bcmsCurrency} _amount - The amount property of a NotificationDetail 
 * @property {BOOLEAN} _isEnabled - The isEnabled property of a NotificationDetail 
 * @property {ARRAY} _contactInfoEnabledIDs - The contactInfoEnabledIDs property of a NotificationDetail 
 */
export default class NotificationDetail extends BCMSRecord {
    _id;
    _notification;
    _account;
    _amount;
    _isEnabled;
    _contactInfoEnabledIDs;

	/**
	 * @function constructor
	 * @param {OBJECT} record - record parameter
	 * @returns {NotificationDetail}
	 * @description constructor function for NotificationDetail 
	 * @memberof NotificationDetail
	 */
    constructor(record) {
        super(record);
    }

    static getByNotificationID(id) {
        return this.initAll().filter(item => item.notification.id === id, []);
    }
}

NotificationDetail.define({
    "recordType": "notificationDetails",
    "recordIDProp" : "id",
	"className" : "NotificationDetail",
    "recordProperties" : [
		/**
		 * @name id
		 * @instance
		 * @property get {INTEGER} - gets the id of notification detail
		 * @property set {INTEGER} - sets the id of notification detail
		 * @memberof NotificationDetail
		 */
        BCMSRecordProperty.mapToString("id", "_id"),

		/**
		 * @name notification
		 * @instance
		 * @property get {notification} - gets the notification of notification detail
		 * @property set {notification} - sets the notification of notification detail
		 * @memberof NotificationDetail
		 */
        BCMSRecordProperty.mapToClass("notification", "_notification", () => Notification),

		/**
		 * @name account
		 * @instance
		 * @property get {account} - gets the account of notification detail
		 * @property set {account} - sets the account of notification detail
		 * @memberof NotificationDetail
		 */
        BCMSRecordProperty.mapToClass("account", "_account", () => Account),

		/**
		 * @name amount
		 * @instance
		 * @property get {BCMSCurrency} - gets the amount of notification detail
		 * @property set {BCMSCurrency} - sets the amount of notification detail
		 * @memberof NotificationDetail
		 */
        BCMSRecordProperty.mapToCurrency("amount", "_amount"),

		/**
		 * @name isEnabled
		 * @instance
		 * @property get {BOOLEAN} - gets the isEnabled of notification detail
		 * @property set {BOOLEAN} - sets the isEnabled of notification detail
		 * @memberof NotificationDetail
		 */
        BCMSRecordProperty.mapToBoolean("isEnabled", "_isEnabled"),

		/**
		 * @name contactInfoEnabledIDs
		 * @instance
		 * @property get {ARRAY} - gets the contactInfoEnabledIDs of notification detail
		 * @property set {ARRAY} - sets the contactInfoEnabledIDs of notification detail
         * @note The mapToArray currently does not work correctly when the toRecord function is called
		 * @memberof NotificationDetail
		 */
        BCMSRecordProperty.mapTo({
            id: "contactInfoEnabledIDs",
            instanceField: "_contactInfoEnabledIDs",
            setter: function(value) {
				this._contactInfoEnabledIDs = value === undefined || Array.isArray(value)
                    ? value : value.split(",");
            },
            toRecord: function() {
                return this._contactInfoEnabledIDs === undefined || this._contactInfoEnabledIDs.length === 0
                    ? ""
                    : this._contactInfoEnabledIDs.reduce((result, item) => `${ result },${ item }`);
            }
        }),

		/**
		 * @name scheduledAlert
		 * @instance
		 * @property get {account} - gets the account of notification detail
		 * @property set {account} - sets the account of notification detail
		 * @memberof NotificationDetail
		 */
		BCMSRecordProperty.mapToClass("scheduledAlert", "_scheduledAlert", () => ScheduledAlert),
    ]
});